import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { useUiStore } from "@/stores/ui";
import { marginToLeverageText, orderSizeText } from "@/lib/engine-properties";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Calendar, CandlestickChart, Code2, LineChart, List, Save, SlidersHorizontal, Square } from "lucide-react";
import { Dialog } from "../../../components/ui/Dialog";
import { PerformancePanel } from "../../../components/backtest/PerformancePanel";
import { runMonteCarlo, type MonteCarlo } from "@/lib/monte-carlo";
import { Pill } from "../../../components/ui/kit";
import { BacktestChart } from "../../../components/charts/BacktestChart";
import { ClosedTradeTable } from "../../../components/TradeTable";
import { BacktestSettingsModal } from "../../../components/backtest/BacktestSettingsModal";
import { StartDateWarning } from "../../../components/StartDateWarning";
import { ScriptEditor } from "../../../components/ScriptEditor";
import { SymbolMarketSelect } from "../../../components/SymbolMarketSelect";
import { DateTimePicker } from "../../../components/DateTimePicker";
import { buildClosedTrades, computeStats } from "@/lib/backtest-stats";
import { maxDrawdownAt, performanceSeries, rangePresetOf, returnsByYear } from "@/lib/backtest-analytics";
import { HISTORY_PRESETS, historyStart } from "@/lib/new-backtest";
import { readCachedPreview, toDateTime } from "@/lib/backtest-run";
import { runSummary, sameSummary, storedSummary } from "@/lib/backtest-summary";
import { useBacktestPreview } from "../../../hooks/useBacktestPreview";
import type { Backtest, BacktestResult, MarketSymbol, ScriptRevision } from "@/api/types";
import { engineType, isLiveInstance, liveInstancesFor } from "@/lib/live";
import { marketProviderLabel } from "@/lib/market-provider";
import { formatScriptRef, parseScriptRef, resolveStrategySource } from "@/lib/script-ref";
import { pinStrategySource, saveValidatedScript, hasPinnedStrategySource } from "@/lib/script-save";
import {
  applyScriptLeverageDefault,
  fetchPineStrategySpecs,
} from "@/lib/strategy-leverage";
import { fillParamDefaults, buildConfigPayload } from "../../../components/DynamicForm";
import {
  ENABLED_MARKET_PROVIDERS,
  isMarketProviderEnabled,
  marketTypeOf,
  marketVariants,
  sortMarketProviders,
  type MarketListing,
  type MarketTradeType,
} from "@/lib/market-provider";
import { normalizeConfigurableParams } from "@/lib/configurable-params";
import { useApi } from "../../../context/ApiContext";
import { useFormatDate } from "@/hooks/useFormatDate";

const DEFAULT_TIMEFRAMES = ["1m", "5m", "15m", "1h", "4h", "1d"];
// v3: the default is now 40%; widths saved under older keys are dropped.
const PINE_WIDTH_KEY = "lab-backtest-pine-width-v3";
const PINE_WIDTH_DEFAULT = 40;
const PINE_WIDTH_MIN = 22;
const PINE_WIDTH_MAX = 70;

type ResultTab = "performance" | "trades";
const TF_CHIPS = ["1m", "5m", "15m", "1h", "4h", "1d"];

/**
 * TV-style backtest workspace: results (left) + editable Pine (right).
 * Save Script validates + revises; Run refreshes stats without leaving the page.
 */
export default function BacktestWorkspace() {
  const openGoLive = useUiStore((st) => st.openGoLive);
  const { id } = useParams();
  const location = useLocation();
  const api = useApi(["backtests", "scripts"]);
  const { dateTimeShort } = useFormatDate();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scriptBusy, setScriptBusy] = useState(false);
  const [source, setSource] = useState("");
  const [sourceReady, setSourceReady] = useState(false);
  const [scriptDirty, setScriptDirty] = useState(false);
  const [scriptMsg, setScriptMsg] = useState<string | null>(null);
  const [scriptError, setScriptError] = useState<string | null>(null);
  const [localBacktest, setLocalBacktest] = useState<Record<string, unknown> | null>(null);
  const [revisions, setRevisions] = useState<ScriptRevision[]>([]);
  const [selectedRev, setSelectedRev] = useState<number>(0);
  const [revLoading, setRevLoading] = useState(false);
  // Results first: the editor opens from the toolbar.
  const [pineCollapsed, setPineCollapsed] = useState(true);
  /** The price chart opens over the page: inline, it caught the page's scrolling. */
  const [chartOpen, setChartOpen] = useState(false);
  const [pineWidthPct, setPineWidthPct] = useState(() => {
    try {
      const raw = localStorage.getItem(PINE_WIDTH_KEY);
      const n = raw ? Number(raw) : PINE_WIDTH_DEFAULT;
      if (!Number.isFinite(n)) return PINE_WIDTH_DEFAULT;
      return Math.min(PINE_WIDTH_MAX, Math.max(PINE_WIDTH_MIN, n));
    } catch {
      return PINE_WIDTH_DEFAULT;
    }
  });
  const [symbol, setSymbol] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [timeframes, setTimeframes] = useState<string[]>(DEFAULT_TIMEFRAMES);
  const [marketProviders, setMarketProviders] = useState<string[]>([...ENABLED_MARKET_PROVIDERS]);
  const [resultTab, setResultTab] = useState<ResultTab>("performance");
  /** Bumped by anything that should re-run the backtest (TradingView-style auto run). */
  const [runTick, setRunTick] = useState(0);
  const requestRun = useCallback(() => setRunTick((t) => t + 1), []);
  /** Identifies the run in flight, so a stopped one can't update the page when it returns. */
  const runSeqRef = useRef(0);
  /** The last run was stopped: results on screen are from an earlier one. */
  const [stopped, setStopped] = useState(false);
  /** Custom dates changed while the popover is open: run once when it closes. */
  const rangeDirtyRef = useRef(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const closeRange = useCallback(() => {
    setRangeOpen(false);
    if (rangeDirtyRef.current) {
      rangeDirtyRef.current = false;
      requestRun();
    }
  }, [requestRun]);
  const rangeRef = useRef<HTMLDivElement>(null);

  // Close the date popover on outside clicks. The calendar is portaled out of
  // #root, so clicks there don't count as outside.
  useEffect(() => {
    if (!rangeOpen) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (rangeRef.current?.contains(t)) return;
      if (!document.getElementById("root")?.contains(t)) return;
      closeRange();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeRange();
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [rangeOpen, closeRange]);

  // Flash save / script feedback inside the Pine panel; clear after a few seconds.
  useEffect(() => {
    if (!scriptMsg && !scriptError) return;
    const t = window.setTimeout(() => {
      setScriptMsg(null);
      setScriptError(null);
    }, 3000);
    return () => window.clearTimeout(t);
  }, [scriptMsg, scriptError]);

  const dirtyRef = useRef(false);
  const loadedPinRef = useRef<string | null>(null);
  const healPinRef = useRef<string | null>(null);
  const marketTouchedRef = useRef(false);
  /** Date range the shown result was computed for (the chart's x-axis). */
  const [ranRange, setRanRange] = useState<{ start: string; end: string | null } | null>(null);
  const scriptBusyRef = useRef(false);
  /** Latest editor text without forcing a React update on every key. */
  const latestSourceRef = useRef("");
  const splitRef = useRef<HTMLDivElement>(null);
  const pineDragRef = useRef(false);
  dirtyRef.current = scriptDirty;
  scriptBusyRef.current = scriptBusy;

  const fromApi = useMemo(() => api.backtests.find((b) => b.id === id), [api.backtests, id]);
  const backtest = useMemo(() => {
    const synced = (location.state as { syncedBacktest?: Record<string, unknown> } | null)
      ?.syncedBacktest;
    // The copy handed over on navigation bridges the gap until the server list
    // catches up. It survives reloads (history state), so only trust it while
    // it is newer than what the server sent.
    const usable =
      synced?.id === id &&
      (!fromApi || Date.parse(String(synced?.updated_at || "")) > Date.parse(String(fromApi.updated_at || "")));
    const base = usable ? { ...fromApi, ...synced } : fromApi;
    if (!base) return undefined;
    if (localBacktest?.id === id) return { ...base, ...localBacktest };
    return base;
  }, [fromApi, id, location.state, localBacktest]);

  // Keep quick-market fields in sync with the merged backtest used for Run.
  const runBacktest = useMemo(() => {
    if (!backtest) return undefined;
    return {
      ...backtest,
      id: String(backtest.id || id),
      // Fall back to the saved values until the controls hydrate, so the first
      // render already matches the cached result.
      symbol: symbol || String(backtest.symbol || ""),
      timeframe: timeframe || String(backtest.timeframe || ""),
      // Prefer the hydrated form controls; fall back to the saved entity so
      // a Run/preview never fires with an empty start_date before hydrate.
      start_date: startDate || toDateTime(backtest.start_date as string) || backtest.start_date,
      end_date: endDate || toDateTime(backtest.end_date as string | null) || backtest.end_date || null,
    };
  }, [backtest, id, symbol, timeframe, startDate, endDate]);

  const preview = useBacktestPreview(runBacktest);
  const result = preview.displayResult as BacktestResult | null | undefined;

  // The price chart loads its candles after every run (never while one is in flight).
  useEffect(() => {
    preview.loadChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.previewResult, backtest?.id]);

  const scriptId = useMemo(() => {
    const meta =
      typeof backtest?.metadata?.ui?.script_id === "string"
        ? backtest.metadata.ui.script_id
        : "";
    const ref = parseScriptRef(String(backtest?.strategy_config?.source || ""));
    return ref?.id || meta || "";
  }, [backtest]);

  const pinUri = String(backtest?.strategy_config?.source || "");

  const pinnedRev = useMemo(() => {
    const ref = parseScriptRef(pinUri);
    if (ref?.rev) return ref.rev;
    return selectedRev || 0;
  }, [pinUri, selectedRev]);

  const refreshRevisions = useCallback(
    async (sid?: string) => {
      const idToLoad = sid || scriptId;
      if (!idToLoad) {
        setRevisions([]);
        return;
      }
      try {
        const list = (await api.commands.listScriptRevisions({ id: idToLoad })) as ScriptRevision[];
        const sorted = (Array.isArray(list) ? list : []).slice().sort((a, b) => b.rev_no - a.rev_no);
        setRevisions(sorted);
      } catch {
        setRevisions([]);
      }
    },
    [api.commands, scriptId]
  );

  useEffect(() => {
    void refreshRevisions();
  }, [refreshRevisions, scriptId]);

  // Hydrate quick market controls from backtest on open / return from Settings.
  useEffect(() => {
    if (!backtest?.id) return;
    marketTouchedRef.current = false;
    setRanRange({
      start: toDateTime(backtest.start_date as string),
      end: toDateTime(backtest.end_date as string | null) || null,
    });
    setSymbol(String(backtest.symbol || ""));
    setTimeframe(String(backtest.timeframe || "1h"));
    setStartDate(toDateTime(backtest.start_date as string));
    setEndDate(toDateTime(backtest.end_date as string | null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backtest?.id, location.key]);

  useEffect(() => {
    let cancelled = false;
    api
      .commands.listConfigurables()
      .then((list: unknown) => {
        if (cancelled) return;
        const mps =
          (list as { market_providers?: string[] } | null)?.market_providers ||
          [...ENABLED_MARKET_PROVIDERS];
        setMarketProviders(
          sortMarketProviders(mps.filter((p) => isMarketProviderEnabled(p)))
        );
      })
      .catch(() => {
        if (!cancelled) setMarketProviders([...ENABLED_MARKET_PROVIDERS]);
      });
    return () => {
      cancelled = true;
    };
  }, [api.commands]);

  useEffect(() => {
    if (!backtest?.market_provider) return;
    let cancelled = false;
    const t = setTimeout(() => {
      const cfg = (backtest.market_config || {}) as unknown as Record<string, unknown>;
      api
        .commands.getMarketTimeframes({
          market_provider: backtest.market_provider || "",
          market_config: cfg,
        })
        .then((tf: unknown) => {
          if (!cancelled) {
            setTimeframes(Array.isArray(tf) && tf.length ? (tf as string[]) : DEFAULT_TIMEFRAMES);
          }
        })
        .catch(() => {
          if (!cancelled) setTimeframes(DEFAULT_TIMEFRAMES);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [api.commands, backtest?.market_provider, backtest?.market_config]);

  /**
   * One market config per market (spot/futures) of an exchange. The backtest's
   * own exchange keeps its other settings; another exchange starts from defaults.
   */
  const marketConfigsFor = useCallback(
    async (prov: string) => {
      const currentProv = String(backtest?.market_provider || "");
      const current = (backtest?.market_config || {}) as unknown as Record<string, unknown>;
      try {
        const raw = await api.commands.getParams({
          category: "market_provider",
          name: prov,
          config: {},
        });
        const specs = normalizeConfigurableParams(raw).specs;
        const base = prov === currentProv ? current : buildConfigPayload(fillParamDefaults({}, specs), specs);
        return marketVariants(base, specs);
      } catch {
        const base = prov === currentProv ? current : {};
        return [{ market: marketTypeOf(base), config: base }];
      }
    },
    [api.commands, backtest?.market_provider, backtest?.market_config]
  );

  const loadSymbolsForProvider = useCallback(
    async (prov: string): Promise<MarketListing[]> => {
      const variants = await marketConfigsFor(prov);
      const lists = await Promise.all(
        variants.map(async ({ market, config }) => {
          const s = await api.commands.getMarketSymbols({
            market_provider: prov,
            market_config: config,
          });
          return (Array.isArray(s) ? (s as MarketSymbol[]) : [])
            .map((row) => row.name)
            .filter(Boolean)
            .map((name): MarketListing => ({ symbol: name, market }));
        })
      );
      return lists.flat();
    },
    [api.commands, marketConfigsFor]
  );

  const handleSymbolMarketChange = useCallback(
    async (next: { symbol: string; provider: string; market: MarketTradeType }) => {
      marketTouchedRef.current = true;
      setSymbol(next.symbol);

      const prevProv = String(backtest?.market_provider || "");
      let market_config = (backtest?.market_config || {}) as unknown as Record<string, unknown>;
      let nextTimeframe = timeframe;

      if (next.provider !== prevProv || next.market !== marketTypeOf(market_config)) {
        const variants = await marketConfigsFor(next.provider);
        market_config = (variants.find((v) => v.market === next.market) ?? variants[0])?.config ?? {};
        try {
          const tf = await api.commands.getMarketTimeframes({
            market_provider: next.provider,
            market_config,
          });
          const list = Array.isArray(tf) && tf.length ? (tf as string[]) : DEFAULT_TIMEFRAMES;
          setTimeframes(list);
          if (!list.includes(timeframe)) {
            nextTimeframe = list[0] || "1h";
            setTimeframe(nextTimeframe);
          }
        } catch {
          setTimeframes(DEFAULT_TIMEFRAMES);
        }
      }

      setLocalBacktest((prev) => ({
        ...(prev && prev.id === id ? prev : backtest || {}),
        id,
        symbol: next.symbol,
        market_provider: next.provider,
        market_config,
        timeframe: nextTimeframe,
      }));
      requestRun();
    },
    [api.commands, backtest, id, timeframe, requestRun, marketConfigsFor]
  );

  function patchMarket(
    partial: {
      symbol?: string;
      timeframe?: string;
      start_date?: string;
      end_date?: string | null;
    },
    /** False while typing custom dates: those run when the popover closes. */
    run = true
  ) {
    marketTouchedRef.current = true;
    if (partial.symbol != null) {
      setSymbol(partial.symbol);
    }
    if (partial.timeframe != null) setTimeframe(partial.timeframe);
    if (partial.start_date != null) setStartDate(partial.start_date);
    if (partial.end_date !== undefined) setEndDate(partial.end_date || "");
    setLocalBacktest((prev) => ({
      ...(prev && prev.id === id ? prev : backtest || {}),
      id,
      ...partial,
    }));
    if (run) requestRun();
    else rangeDirtyRef.current = true;
  }

  // Sync dropdown selection to the pinned revision when not dirty.
  useEffect(() => {
    if (dirtyRef.current) return;
    const ref = parseScriptRef(pinUri);
    if (ref?.rev) setSelectedRev(ref.rev);
    else if (revisions[0]?.rev_no) setSelectedRev(revisions[0].rev_no);
  }, [pinUri, revisions]);

  // Load editable source once per pinned URI — skip while dirty / already loaded (avoids socket flicker).
  // When strategy_config.source is missing but metadata.ui.script_id exists, fall back to that script tip.
  useEffect(() => {
    if (!backtest?.id) return;
    if (dirtyRef.current) return;

    const fallbackUri =
      !pinUri && scriptId
        ? formatScriptRef(scriptId, revisions[0]?.rev_no || selectedRev || 1)
        : pinUri;
    if (!fallbackUri && !pinUri) {
      // Still waiting for scriptId / revisions.
      if (!scriptId) {
        setSource("");
        latestSourceRef.current = "";
        setSourceReady(true);
      }
      return;
    }
    const loadUri = fallbackUri;
    if (loadedPinRef.current === loadUri && sourceReady) return;

    let cancelled = false;
    const firstLoad = !sourceReady;
    if (firstLoad) setSourceReady(false);

    void (async () => {
      try {
        const resolved = await resolveStrategySource(api.commands, loadUri, api.scripts);
        if (cancelled || dirtyRef.current) return;
        loadedPinRef.current = loadUri;
        setSource(resolved);
        latestSourceRef.current = resolved;
        setSourceReady(true);

        // Persist a missing pin so Run / preview don't fail with script "source" is required.
        if (
          !hasPinnedStrategySource(backtest.strategy_config as unknown as Record<string, unknown>) &&
          scriptId &&
          healPinRef.current !== backtest.id
        ) {
          healPinRef.current = String(backtest.id);
          const rev = parseScriptRef(loadUri)?.rev || revisions[0]?.rev_no || 1;
          const healed = {
            ...(backtest.strategy_config || {}),
            source: formatScriptRef(scriptId, rev),
          };
          try {
            const updated = (await api.commands.updateBacktest({
              id: backtest.id!,
              strategy_config: healed,
              enabled: false,
            })) as unknown as Record<string, unknown>;
            if (!cancelled) setLocalBacktest(updated);
          } catch {
            healPinRef.current = null;
            /* keep editor usable; Run will surface the error */
          }
        }
      } catch {
        if (cancelled || dirtyRef.current) return;
        loadedPinRef.current = loadUri;
        const fallback = loadUri.startsWith("script://") ? "" : loadUri;
        setSource(fallback);
        latestSourceRef.current = fallback;
        setSourceReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backtest?.id, pinUri, scriptId, revisions]);

  async function handleSelectRevision(revNo: number) {
    if (!scriptId || !Number.isFinite(revNo) || revNo < 1) return;
    if (revNo === selectedRev && !scriptDirty) return;
    setRevLoading(true);
    setScriptError(null);
    setScriptMsg(null);
    try {
      const full = (await api.commands.getScriptRevision({
        id: scriptId,
        rev_no: revNo,
      })) as ScriptRevision;
      const next = full.source ?? "";
      setSource(next);
      latestSourceRef.current = next;
      setSelectedRev(revNo);
      const pinned = parseScriptRef(pinUri)?.rev;
      if (pinned != null && revNo === pinned) {
        // Back to the pinned revision — clean.
        setScriptDirty(false);
        loadedPinRef.current = pinUri;
      } else {
        // Older (or other) revision loaded into the editor — Save creates a new tip.
        setScriptDirty(true);
      }
    } catch (e: unknown) {
      setScriptError(e instanceof Error ? e.message : "Failed to load revision");
    } finally {
      setRevLoading(false);
    }
  }

  const closed = useMemo(() => {
    if (!result?.trades) return [];
    return buildClosedTrades(
      result.trades,
      result.last_close || 0,
      result.last_date || new Date().toISOString(),
      result.fee_rate || 0,
      result.fiat_start ?? result.initial_fiat ?? 0,
      result.fiat_end ?? result.estimated_end_fiat
    );
  }, [result]);

  const stats = useMemo(() => {
    if (!result) return null;
    return computeStats(
      closed,
      result.fiat_start ?? result.initial_fiat ?? 0,
      result.fiat_end ?? result.estimated_end_fiat ?? 0,
      result as BacktestResult & Record<string, number | undefined>
    );
  }, [result, closed]);

  // The backtest list shows each backtest's last result: keep its headline numbers on the backtest.
  // Saved only when they change, so opening a backtest again does not move it up the list.
  const savedSummaryRef = useRef<string>("");
  useEffect(() => {
    const btId = typeof backtest?.id === "string" ? backtest.id : "";
    // A run stopped halfway is not a result.
    if (!btId || preview.previewing || loading || stopped || !preview.previewResult || !stats) return;
    const next = runSummary(stats);
    const metadata = (backtest?.metadata || {}) as NonNullable<Backtest["metadata"]>;
    const key = `${btId}:${JSON.stringify({ ...next, at: "" })}`;
    if (savedSummaryRef.current === key || sameSummary(storedSummary(metadata), next)) return;
    savedSummaryRef.current = key;
    void api.commands
      .updateBacktest({ id: btId, metadata: { ...metadata, ui: { ...(metadata.ui || {}), summary: next } } })
      .catch(() => {
        savedSummaryRef.current = "";
      });
  }, [api.commands, backtest?.id, backtest?.metadata, loading, preview.previewResult, preview.previewing, stats, stopped]);

  // The server runs this only on its command line; the trades are all it needs.
  const monteCarlo = useMemo(() => {
    if (!result || !stats) return null;
    const compounding = result.engine_type === "margin" && String(result.default_qty || "").trim().endsWith("%");
    return runMonteCarlo(closed, result.fiat_start ?? result.initial_fiat ?? 0, compounding, stats.max_drawdown_pct);
  }, [result, stats, closed]);

  const perf = useMemo(
    () =>
      result
        ? performanceSeries(closed, result.fiat_start ?? result.initial_fiat ?? 0, {
            start: ranRange?.start || (backtest?.start_date as string),
            end: ranRange?.end || result.last_date || null,
          })
        : [],
    [result, closed, ranRange, backtest?.start_date]
  );
  const years = useMemo(() => returnsByYear(perf), [perf]);

  const showProgress = preview.previewing || (backtest?.status === "running" && !result);

  const handleSourceChange = useCallback((next: string) => {
    // Ignore no-op flushes (e.g. blur after Ctrl+S) so Run isn't blocked by a false dirty.
    if (next === latestSourceRef.current) return;
    latestSourceRef.current = next;
    if (!dirtyRef.current) {
      // Mark dirty synchronously: the editor reports its text on blur, i.e.
      // during the same click that runs, before React re-renders.
      dirtyRef.current = true;
      setScriptDirty(true);
      setScriptMsg(null);
      setScriptError(null);
    }
  }, []);

  const handlePineResizeStart = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const split = splitRef.current;
    if (!split) return;
    pineDragRef.current = true;
    const pointerId = e.pointerId;
    (e.target as HTMLElement).setPointerCapture?.(pointerId);

    const onMove = (ev: PointerEvent) => {
      if (!pineDragRef.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      if (rect.width <= 0) return;
      const pct = ((rect.right - ev.clientX) / rect.width) * 100;
      setPineWidthPct(Math.min(PINE_WIDTH_MAX, Math.max(PINE_WIDTH_MIN, pct)));
    };
    const onUp = () => {
      pineDragRef.current = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setPineWidthPct((w) => {
        try {
          localStorage.setItem(PINE_WIDTH_KEY, String(Math.round(w * 10) / 10));
        } catch {
          /* ignore */
        }
        return w;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  /** Saves the editor as a new script revision; returns the updated backtest, or null on failure. */
  const handleSaveScript = useCallback(async (): Promise<Record<string, unknown> | null> => {
    if (!backtest || !id) return null;
    const text = latestSourceRef.current || source;
    if (!text.trim()) {
      setScriptError("Script source is required");
      return null;
    }
    setScriptBusy(true);
    scriptBusyRef.current = true;
    setScriptError(null);
    setScriptMsg(null);
    try {
      const linkedName = scriptId
        ? api.scripts.find((s) => s.id === scriptId)?.name
        : undefined;
      const saved = await saveValidatedScript(api.commands, {
        scriptId: scriptId || null,
        name: String(linkedName || backtest.name || "Strategy"),
        source: text,
      });

      // Rebuild inputs from the new source defaults — do NOT keep old leverage/etc.
      const freshSpecs = await fetchPineStrategySpecs(api.commands, saved.source);
      const filled = applyScriptLeverageDefault(
        freshSpecs,
        fillParamDefaults({ source: saved.source }, freshSpecs)
      );
      const strategy_config = pinStrategySource(filled, saved);

      const metadata = {
        ...(backtest.metadata || {}),
        ui: {
          ...((backtest.metadata as { ui?: Record<string, unknown> } | null)?.ui || {}),
          mode: "lab" as const,
          script_id: saved.id,
        },
      };

      // Script Save must not rewrite engine_config — Settings → Properties owns
      // initial/pyramiding/qty. Seeding from Pine here was resetting them on every
      // script save / refresh cycle.
      const updated = (await api.commands.updateBacktest({
        id,
        strategy_config,
        metadata,
        enabled: false,
      })) as unknown as Record<string, unknown>;

      const pin = formatScriptRef(saved.id, saved.rev_no);
      loadedPinRef.current = pin;
      try {
        sessionStorage.removeItem(`lab-preview:${id}`);
      } catch {
        /* ignore */
      }

      setLocalBacktest(updated);
      setSource(saved.source);
      latestSourceRef.current = saved.source;
      dirtyRef.current = false;
      setScriptDirty(false);
      setSelectedRev(saved.rev_no);
      setScriptMsg(`Saved (rev ${saved.rev_no})`);
      void refreshRevisions(saved.id);
      return updated;
    } catch (e: unknown) {
      setScriptError(e instanceof Error ? e.message : "Failed to save script");
      return null;
    } finally {
      scriptBusyRef.current = false;
      setScriptBusy(false);
    }
  }, [api.commands, api.scripts, backtest, id, scriptId, source, refreshRevisions]);

  async function handleRun(override?: Record<string, unknown>) {
    if (!runBacktest) return;
    // Run what's in the editor: save it first so the backtest uses the latest script.
    if (dirtyRef.current) {
      if (scriptBusyRef.current) return;
      const updated = await handleSaveScript();
      if (!updated) return;
      override = {
        ...(override || {}),
        strategy_config: updated.strategy_config,
        metadata: updated.metadata,
      };
    }
    if (!symbol.trim()) {
      setScriptError("Symbol is required");
      return;
    }
    if (!timeframe.trim()) {
      setScriptError("Timeframe is required");
      return;
    }
    if (!startDate) {
      setScriptError("Start date is required");
      return;
    }
    const seq = ++runSeqRef.current;
    setStopped(false);
    setLoading(true);
    preview.setError(null);
    // Let the running state paint first. A timer, not requestAnimationFrame:
    // frames don't fire in a background tab, which would leave the run waiting.
    await new Promise<void>((resolve) => window.setTimeout(resolve, 30));
    try {
      const ran = override ? { ...runBacktest, ...override } : runBacktest;
      await preview.run(ran);
      if (seq !== runSeqRef.current) return;
      marketTouchedRef.current = false;
      setRanRange({ start: String(ran.start_date || ""), end: (ran.end_date as string | null) || null });
    } catch (e: unknown) {
      if (seq === runSeqRef.current) preview.setError(e instanceof Error ? e.message : "Run failed");
    } finally {
      if (seq === runSeqRef.current) setLoading(false);
    }
  }

  /** Stop button: give the page back right away; the late answer is dropped. */
  function handleStop() {
    runSeqRef.current += 1;
    preview.cancel();
    setLoading(false);
    setStopped(true);
  }

  const handleRunRef = useRef(handleRun);
  handleRunRef.current = handleRun;

  // Auto run: symbol, exchange, timeframe, dates and saved script changes all land here.
  useEffect(() => {
    if (runTick === 0) return;
    void handleRunRef.current();
  }, [runTick]);

  /**
   * Save the script as a new revision and run it. Does nothing without
   * changes, so the Save button can stay enabled: the editor reports edits a
   * moment late (and on blur), and a disabled button would swallow a quick click.
   */
  const saveAndRun = useCallback(async () => {
    if (scriptBusyRef.current || !dirtyRef.current) return;
    await handleRunRef.current();
  }, []);

  // Opening a backtest with nothing to show (no cached result) runs it.
  const openRunDoneRef = useRef<string | null>(null);
  useEffect(() => {
    if (!runBacktest?.id || !symbol || !timeframe || !startDate) return;
    if (openRunDoneRef.current === runBacktest.id) return;
    openRunDoneRef.current = String(runBacktest.id);
    if (autoRun || preview.previewing || readCachedPreview(runBacktest as never)) return;
    requestRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runBacktest?.id, symbol, timeframe, startDate]);

  // “Create & run” from the New backtest dialog: run once when the form is hydrated,
  // then drop the flag so a reload doesn't run again.
  const autoRunDoneRef = useRef(false);
  const autoRun = (location.state as { autoRun?: boolean } | null)?.autoRun === true;
  useEffect(() => {
    if (!autoRun || autoRunDoneRef.current || !runBacktest || !symbol || !timeframe || !startDate) return;
    autoRunDoneRef.current = true;
    const { autoRun: _drop, ...rest } = (location.state as unknown as Record<string, unknown>) || {};
    navigate(location.pathname, { replace: true, state: rest });
    void handleRunRef.current();
  }, [autoRun, runBacktest, symbol, timeframe, startDate, location.pathname, location.state, navigate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== "Enter") return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "TEXTAREA" || t.isContentEditable)) {
        // Allow Cmd+Enter from Monaco / inputs to run as well.
      }
      e.preventDefault();
      void handleRunRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!api.ready) return <div className="card">Loading…</div>;
  if (!backtest) return <div className="card">Backtest not found</div>;
  if (isLiveInstance(backtest)) return <Navigate to={`/live/${id}`} replace />;

  // Already trading: no "Go live" prompt for it.
  const liveNow = !!fromApi && liveInstancesFor(fromApi, api.backtests).some((l) => l.enabled && l.status !== "stopped");

  // Run is independent of chart candle fetch — only the one-shot preview gates it.
  const busy = loading || preview.previewing || scriptBusy;
  const running = preview.previewing || loading;
  const rangeStart = startDate || toDateTime(backtest.start_date as string);
  const rangeEnd = endDate || null;
  const rangePreset = rangePresetOf(rangeStart, rangeEnd);
  const ddAt = maxDrawdownAt(perf);
  const tfChips = TF_CHIPS.filter((tf) => timeframes.includes(tf));
  const tfMore = timeframes.filter((tf) => !tfChips.includes(tf));

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Market, range and properties — everything Run depends on, in one bar */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--border)] px-3 py-2">
        <SymbolMarketSelect
          symbol={symbol}
          provider={String(backtest.market_provider || "binance")}
          providers={marketProviders}
          loadSymbols={loadSymbolsForProvider}
          onChange={(next) => void handleSymbolMarketChange(next)}
          placeholder="Symbol…"
          searchPlaceholder="Search symbols…"
          title="Select symbol"
          disabled={busy}
          variant="chip"
          market={engineType(backtest)}
          detail={`${marketProviderLabel(String(backtest.market_provider || "binance"))} ${
            engineType(backtest) === "futures" ? "Futures" : "Spot"
          }`}
        />

        <div className={seg} role="group" aria-label="Timeframe">
          {(tfChips.includes(timeframe) || tfMore.includes(timeframe) ? tfChips : [timeframe, ...tfChips]).map((tf) => (
            <button
              key={tf}
              type="button"
              disabled={busy}
              aria-pressed={tf === timeframe}
              className={segBtn(tf === timeframe)}
              onClick={() => patchMarket({ timeframe: tf })}
            >
              {tf}
            </button>
          ))}
          {tfMore.length > 0 && (
            <select
              aria-label="More timeframes"
              disabled={busy}
              value={tfMore.includes(timeframe) ? timeframe : ""}
              onChange={(e) => e.target.value && patchMarket({ timeframe: e.target.value })}
              className={`!h-auto !min-h-0 !w-auto !border-0 !bg-transparent !py-0.5 !pl-1.5 !pr-5 !text-xs ${
                tfMore.includes(timeframe) ? "font-medium text-[var(--primary)]" : "text-[var(--text-muted)]"
              }`}
            >
              <option value="">More</option>
              {tfMore.map((tf) => (
                <option key={tf} value={tf}>
                  {tf}
                </option>
              ))}
            </select>
          )}
        </div>

        <div ref={rangeRef} className="relative">
          <div className="flex items-center gap-2 rounded-[9px] border border-[var(--border)] bg-[var(--bg-secondary)] py-0.5 pl-0.5 pr-2.5 text-xs">
            <div className="flex" role="group" aria-label="Date range">
              {HISTORY_PRESETS.map(({ id: p, label }) => (
                <button
                  key={p}
                  type="button"
                  disabled={busy}
                  aria-pressed={rangeOpen ? p === "custom" : p === rangePreset}
                  className={segBtn(rangeOpen ? p === "custom" : p === rangePreset)}
                  onClick={() => {
                    if (p === "custom") {
                      if (rangeOpen) closeRange();
                      else setRangeOpen(true);
                      return;
                    }
                    rangeDirtyRef.current = false;
                    setRangeOpen(false);
                    patchMarket({ start_date: historyStart(p), end_date: null });
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-1 rounded-[7px] px-1.5 py-0.5 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
              onClick={() => (rangeOpen ? closeRange() : setRangeOpen(true))}
              title="Set start and end date"
              aria-expanded={rangeOpen}
            >
              <Calendar size={12} />
              {monthYear(rangeStart) || "Start"} → {rangeEnd ? monthYear(rangeEnd) : "now"}
            </button>
          </div>
          {rangeOpen && (
            <div className="absolute left-0 top-[calc(100%+6px)] z-30 flex w-[300px] flex-col gap-3 rounded-[12px] border border-[var(--border)] bg-[var(--bg-card)] p-3.5 shadow-lg">
              <label className="flex flex-col gap-1.5">
                <span className={fieldLabel}>Start</span>
                <DateTimePicker value={startDate} disabled={busy} onChange={(v) => patchMarket({ start_date: v }, false)} />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className={fieldLabel}>End</span>
                <DateTimePicker value={endDate} disabled={busy} clearable onChange={(v) => patchMarket({ end_date: v || null }, false)} />
                <span className="text-[11px] text-[var(--text-muted)]">Leave empty to run up to now.</span>
              </label>
              <button type="button" className="btn btn-primary btn-sm self-end" onClick={closeRange}>
                Done
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          disabled={busy}
          onClick={() => setSettingsOpen(true)}
          className="btn btn-soft btn-sm"
          title="Backtest properties"
        >
          <SlidersHorizontal size={14} />
          {propsSummary(backtest).map((part, i) => (
            <span key={i} className="num">
              {i > 0 && <span className="mr-1.5 text-[var(--text-disabled)]">·</span>}
              {part}
            </span>
          ))}
        </button>

        <div className="ml-auto flex items-center gap-2.5">
          {result && stats && (
            <button type="button" className="btn btn-soft btn-sm" onClick={() => setChartOpen(true)}>
              <CandlestickChart size={14} />
              Open chart
            </button>
          )}
          {/* Editor switch: the label says what a click does; the fill says it's open. */}
          <button
            type="button"
            role="switch"
            aria-checked={!pineCollapsed}
            className="btn btn-soft btn-sm"
            style={
              pineCollapsed
                ? undefined
                : { borderColor: "var(--primary)", background: "var(--primary-soft)", color: "var(--primary)" }
            }
            onClick={() => setPineCollapsed((c) => !c)}
          >
            <Code2 size={14} />
            {pineCollapsed ? "Open editor" : "Close editor"}
            {scriptDirty && <span className="h-1.5 w-1.5 rounded-full bg-[var(--warning)]" aria-label="unsaved changes" />}
          </button>
        </div>
      </div>

      {preview.error && (
        <div className="shrink-0 border-b border-[var(--border)] bg-[var(--danger-bg)] px-3 py-2 text-sm text-[var(--danger)]">
          {preview.error}
        </div>
      )}

      <div ref={splitRef} className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Results */}
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Run status floats over the results so it never changes the toolbar's line breaks. */}
          <div role="status" aria-live="polite" className="pointer-events-none absolute right-3 top-2.5 z-20 flex items-center [&>*]:pointer-events-auto [&>*]:shadow-[var(--shadow-md)]">
            {running ? (
              <button
                type="button"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-[9px] border border-[var(--border)] bg-[var(--bg-card)] py-1 pl-2.5 pr-1.5 text-xs font-medium text-[var(--text-primary)] hover:border-[color-mix(in_srgb,var(--danger)_40%,var(--border))]"
                onClick={handleStop}
                title="Stop this run"
                aria-label="Backtesting. Stop"
              >
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-[var(--border)] border-t-[var(--primary)]" />
                Backtesting
                {/* The stop mark is always visible: the whole control stops the run. */}
                <span className="ml-0.5 grid h-4 w-4 place-items-center rounded-[4px] bg-[color-mix(in_srgb,var(--danger)_14%,transparent)] text-[var(--danger)]">
                  <Square size={8} fill="currentColor" />
                </span>
              </button>
            ) : stopped ? (
              <button
                type="button"
                className="btn btn-soft btn-sm whitespace-nowrap !bg-[var(--bg-card)]"
                disabled={busy}
                onClick={() => void handleRun()}
                title="The results below are from the run before."
              >
                <span className="font-normal text-[var(--text-muted)]">Stopped</span>
                Run again
              </button>
            ) : scriptBusy ? (
              <span className="whitespace-nowrap rounded-[9px] border border-[var(--border)] bg-[var(--bg-card)] px-2.5 py-1 text-xs text-[var(--text-muted)]">Saving…</span>
            ) : null}
          </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
          {result && !preview.previewing && <StartDateWarning result={result} />}
          {result?.warning && (
            <div className="shrink-0 border-b border-[var(--border)] bg-[var(--warning-bg)] px-3 py-2 text-sm text-[var(--warning)]">
              {result.warning}
            </div>
          )}

          {result && stats ? (
            <>
              <div
                className={`grid shrink-0 grid-cols-2 border-b border-[var(--border)] ${monteCarlo ? "md:grid-cols-5" : "md:grid-cols-4"} ${running || stopped ? "opacity-50" : ""}`}
              >
                <Stat
                  label="Net profit"
                  tone={stats.net_profit >= 0 ? "up" : "down"}
                  value={`${stats.net_profit >= 0 ? "+" : "−"}$${money(Math.abs(stats.net_profit))}`}
                  sub={
                    <>
                      {pct(stats.net_profit_pct)}
                    </>
                  }
                />
                <Stat
                  label="Max drawdown"
                  tone="down"
                  value={`−${Math.abs(stats.max_drawdown_pct).toFixed(1)}%`}
                  sub={
                    <>
                      −${money(Math.abs(stats.max_drawdown))}
                      {ddAt != null && <> · {monthYear(new Date(ddAt).toISOString())}</>}
                    </>
                  }
                />
                <Stat label="Profit factor" value={stats.profit_factor.toFixed(2)} sub="Above 1.3 is good" />
                <Stat
                  label="Win rate"
                  value={`${(stats.win_rate * 100).toFixed(1)}%`}
                  sub={`${stats.winning_trades.toLocaleString()} / ${(stats.winning_trades + stats.losing_trades).toLocaleString()} trades`}
                />
                {monteCarlo && (
                  <Stat
                    label="If the trades came in another order"
                    wrap
                    value={`−${monteCarlo.p95.toFixed(1)}%`}
                    badge={<Pill tone={MC_GRADE[mcGrade(monteCarlo)].tone}>{MC_GRADE[mcGrade(monteCarlo)].label}</Pill>}
                    sub={
                      <>
                        <span className="font-sans">
                          Worst drawdown over {monteCarlo.iterations.toLocaleString()} shuffles of the same trades (Monte Carlo).
                        </span>{" "}
                        {monteCarlo.riskOfRuin > 5
                          ? `Ruin risk ${monteCarlo.riskOfRuin.toFixed(0)}%`
                          : monteCarlo.verdict === "robust"
                            ? `Typical −${monteCarlo.p50.toFixed(1)}%`
                            : `Typical −${monteCarlo.p50.toFixed(1)}%, yours was lucky`}
                      </>
                    }
                  />
                )}
              </div>

              <div className="flex shrink-0 items-center gap-2 border-b border-[var(--border)] px-3">
              {/* Big enough to notice: the trades are one click away from the charts. */}
              <div className="flex gap-1" role="tablist">
                {(["performance", "trades"] as const).map((t) => {
                  const Icon = t === "performance" ? LineChart : List;
                  return (
                    <button
                      key={t}
                      type="button"
                      role="tab"
                      aria-selected={resultTab === t}
                      onClick={() => setResultTab(t)}
                      className={`-mb-px flex cursor-pointer items-center gap-2 border-b-2 px-4 py-3 text-[15px] font-semibold ${
                        resultTab === t
                          ? "border-[var(--primary)] text-[var(--text-primary)]"
                          : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                      }`}
                    >
                      <Icon size={16} className={resultTab === t ? "text-[var(--primary)]" : ""} />
                      {t === "performance" ? "Performance" : "Trades"}
                      {t === "trades" && closed.length > 0 && (
                        <span
                          className={`num rounded-full px-2 py-0.5 text-xs font-semibold ${
                            resultTab === t ? "bg-[var(--primary-soft)] text-[var(--primary)]" : "bg-[var(--bg-hover)] text-[var(--text-secondary)]"
                          }`}
                        >
                          {closed.length.toLocaleString()}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              </div>

              {/* Grows to the page's height, so the performance charts can use it. */}
              <div className={`flex flex-1 flex-col ${running || stopped ? "opacity-50" : ""}`}>
                {resultTab === "performance" ? (
                  <PerformancePanel series={perf} years={years} />
                ) : (
                  <div className="p-3">
                    <ClosedTradeTable
                      trades={closed}
                      exportFilename={`${String(backtest.name || "backtest").replace(/[^\w.-]+/g, "_")}-trades.csv`}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-16 text-center">
              {stopped ? (
                <>
                  <div className="text-sm text-[var(--text-secondary)]">Stopped before it finished.</div>
                  <button type="button" className="btn btn-soft btn-sm" disabled={busy} onClick={() => void handleRun()}>
                    Run again
                  </button>
                </>
              ) : showProgress || !preview.error ? (
                <>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--border)] border-t-[var(--primary)]" />
                  <div className="text-sm text-[var(--text-secondary)]">Backtesting…</div>
                </>
              ) : (
                <>
                  <div className="text-sm text-[var(--text-secondary)]">The backtest didn't finish.</div>
                  <button type="button" className="btn btn-soft btn-sm" disabled={busy} onClick={() => void handleRun()}>
                    Try again
                  </button>
                </>
              )}
            </div>
          )}

          {chartOpen && (
            <Dialog title="Price chart" className="max-w-[1200px]" onClose={() => setChartOpen(false)}>
              {/* Candles with the trades and the script's drawings. */}
              <div className="relative h-[min(70vh,640px)]">
                <BacktestChart
                  accumulator={preview.streamAcc}
                  version={preview.streamVersion}
                  streamStatus={running && !preview.streamAcc ? "connecting" : preview.streamStatus}
                  barCount={preview.streamBarCount}
                  historyLoading={preview.historyLoading}
                  historyBarCount={preview.historyBarCount}
                />
              </div>
            </Dialog>
          )}

          {result && stats && stats.net_profit > 0 && !running && !stopped && !liveNow && (
            <div className="mt-auto flex shrink-0 flex-wrap items-center gap-2.5 border-t border-[var(--border)] bg-[color-mix(in_srgb,var(--primary)_6%,var(--bg-secondary))] px-3.5 py-2.5">
              <span className="min-w-0 flex-1 text-xs text-[var(--text-muted)]">
                <b className="font-medium text-[var(--text-primary)]">Happy with it?</b> Run it on an exchange.
              </span>
              <button type="button" className="btn btn-primary" onClick={() => openGoLive(id)}>
                Go live →
              </button>
            </div>
          )}
        </div>
        </div>

        {/* Pine editor */}
        {!pineCollapsed && (
          <>
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize editor"
              title="Drag to resize"
              onPointerDown={handlePineResizeStart}
              className="group relative hidden w-1.5 shrink-0 cursor-col-resize touch-none border-l border-[var(--border)] lg:block"
            >
              <span className="absolute inset-y-0 left-0 w-px bg-transparent transition-colors group-hover:bg-[var(--primary)] group-active:bg-[var(--primary)]" />
            </div>
            <div
              className="flex min-h-[420px] min-w-0 w-full flex-col overflow-hidden border-t border-[var(--border)] bg-[var(--bg-secondary)] lg:min-h-0 lg:w-[var(--pine-w)] lg:flex-none lg:border-t-0"
              style={{ "--pine-w": `${pineWidthPct}%` } as CSSProperties}
            >
              <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--border)] px-3 py-1.5">
                {scriptDirty && <Pill tone="warn" dot={false}>unsaved</Pill>}
                {scriptId && revisions.length > 0 && (
                  <select
                    className="!h-auto !min-h-0 !w-auto max-w-[10rem] !py-0.5 !text-xs"
                    value={selectedRev || pinnedRev || ""}
                    disabled={busy || revLoading || !sourceReady}
                    onChange={(e) => void handleSelectRevision(parseInt(e.target.value, 10))}
                    title="Versions"
                  >
                    {revisions.map((r) => {
                      const latest = revisions[0]?.rev_no === r.rev_no;
                      const when = r.created_at ? dateTimeShort(r.created_at) : "";
                      return (
                        <option key={r.rev_no} value={r.rev_no}>
                          v{r.rev_no}
                          {latest ? " (latest)" : ""}
                          {when ? ` · ${when}` : ""}
                        </option>
                      );
                    })}
                  </select>
                )}
                <div className="ml-auto flex items-center gap-1">
                  <button
                    type="button"
                    className={`btn btn-sm ${scriptDirty ? "btn-primary" : "btn-ghost"}`}
                    disabled={busy || !sourceReady}
                    onClick={() => void saveAndRun()}
                    title="Cmd/Ctrl+S"
                  >
                    <Save size={13} />
                    {scriptBusy ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
              <div className="relative min-h-0 flex-1">
                {(scriptError || scriptMsg) && (
                  <div
                    role="status"
                    className={`pointer-events-none absolute left-2 right-2 top-2 z-10 rounded-md px-3 py-1.5 text-xs shadow-sm ${
                      scriptError
                        ? "bg-[var(--danger-bg)] text-[var(--danger)]"
                        : "bg-[color-mix(in_srgb,var(--primary)_18%,var(--bg-card))] text-[var(--primary)]"
                    }`}
                  >
                    {scriptError || scriptMsg}
                  </div>
                )}
                {sourceReady ? (
                  <ScriptEditor
                    value={source}
                    onChange={handleSourceChange}
                    onSave={() => {
                      if (scriptBusyRef.current) return;
                      void saveAndRun();
                    }}
                    height="100%"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-[var(--text-muted)]">Loading script…</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <BacktestSettingsModal
        open={settingsOpen}
        backtest={backtest as Backtest}
        onClose={() => setSettingsOpen(false)}
        outputs={result?.outputs}
        outputsWaiting={Boolean(showProgress && !result?.outputs)}
        onSaved={(updated) => {
          setLocalBacktest(updated as unknown as unknown as Record<string, unknown>);
          // Drop the cached result so the re-run can't show a stale PnL.
          try {
            sessionStorage.removeItem(`lab-preview:${id}`);
          } catch {
            /* ignore */
          }
          const u = updated as unknown as unknown as Record<string, unknown>;
          void handleRunRef.current({
            name: u.name,
            strategy_config: u.strategy_config,
            engine_provider: u.engine_provider,
            engine_config: u.engine_config,
          });
        }}
      />
    </div>
  );
}

const seg = "flex items-center rounded-[9px] border border-[var(--border)] bg-[var(--bg-secondary)] p-0.5";
const fieldLabel = "text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--text-muted)]";

function segBtn(on: boolean) {
  return `cursor-pointer rounded-[7px] px-2 py-0.5 text-xs disabled:cursor-not-allowed disabled:opacity-60 ${
    on
      ? "bg-[var(--primary-soft)] font-medium text-[var(--primary)]"
      : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
  }`;
}

function Stat({
  label,
  value,
  sub,
  tone,
  color: colorProp,
  badge,
  wrap,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  tone?: "up" | "down";
  /** Overrides the tone color. */
  color?: string;
  /** Shown next to the value, e.g. a Good / Caution / Risky chip. */
  badge?: ReactNode;
  /** Let the small text run over two lines instead of cutting it. */
  wrap?: boolean;
}) {
  const color = colorProp || (tone === "up" ? "var(--success)" : tone === "down" ? "var(--danger)" : "var(--text-primary)");
  return (
    <div className="min-w-0 border-b border-r border-[var(--divider)] px-3.5 py-3 md:border-b-0 [&:nth-child(2n)]:border-r-0 md:[&:nth-child(2n)]:border-r md:last:!border-r-0">
      <div className="whitespace-nowrap text-[11px] text-[var(--text-muted)]">{label}</div>
      <div className="mt-0.5 flex flex-wrap items-center gap-2">
        <span className="num text-[17px] font-semibold" style={{ color }}>
          {value}
        </span>
        {badge}
      </div>
      {sub != null && <div className={`num text-[11px] text-[var(--text-muted)] ${wrap ? "leading-snug" : "truncate"}`}>{sub}</div>}
    </div>
  );
}

/**
 * Monte Carlo at a glance. Risky: the account is often lost, or the worst case
 * takes half of it. Caution: the backtest's own drawdown was a lucky one, ruin
 * is possible, or the worst case takes a quarter. Otherwise good.
 */
function mcGrade(mc: MonteCarlo): "good" | "caution" | "risky" {
  if (mc.riskOfRuin > 20 || mc.p95 >= 50) return "risky";
  if (mc.verdict !== "robust" || mc.riskOfRuin > 5 || mc.p95 >= 25) return "caution";
  return "good";
}
const MC_GRADE = {
  good: { tone: "ok", label: "Good" },
  caution: { tone: "warn", label: "Caution" },
  risky: { tone: "bad", label: "Risky" },
} as const;

const moneyFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const money = (n: number) => moneyFmt.format(n);
const pct = (n: number) => `${n >= 0 ? "+" : "−"}${Math.abs(n).toFixed(1)}%`;

function monthYear(v: string | null | undefined): string {
  if (!v) return "";
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : v.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

/** "$1,000,000 · 1 contract · 1x · fee 0.1%" from the saved engine config. */
function propsSummary(bt: { engine_config?: Record<string, unknown> }): string[] {
  const cfg = (bt.engine_config || {}) as unknown as Record<string, unknown>;
  const parts: string[] = [];
  const capital = Number(cfg.initial ?? cfg.fiat);
  if (Number.isFinite(capital) && capital > 0) parts.push(`$${moneyFmt.format(capital)}`);
  if (cfg.qty != null && cfg.qty !== "") parts.push(orderSizeText(cfg.qty, cfg.qty_unit));
  // Leverage as TradingView writes it; no margin requirement at all is "Infinity".
  const leverage = marginToLeverageText(cfg.margin_long);
  if (leverage) parts.push(leverage === "Infinity" ? "leverage Infinity" : leverage);
  const fee = Number(cfg.fee);
  if (Number.isFinite(fee)) parts.push(`fee ${fee}${(cfg.fee_unit || "percent") === "percent" ? "%" : " USD"}`);
  return parts.length ? parts : ["Properties"];
}
